import { createSelector } from 'reselect';
import every from 'lodash/every';
import isFunction from 'lodash/isFunction';
import some from 'lodash/some';
import values from 'lodash/values';
import type { FieldKey, Field } from '@atlassian/jira-polaris-domain-field/src/field/types.tsx';
import type { LocalIssueId } from '@atlassian/jira-polaris-domain-idea/src/idea/types.tsx';
import {
	TEXT_FIELD_FILTER,
	type Filter,
	type FilterGroup,
	type FilterOperator,
	type TextFilter,
} from '@atlassian/jira-polaris-domain-view/src/filter/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FIELD_TYPES } from '@atlassian/jira-polaris-domain-field/src/field-types/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import type { Props, State } from '../types.tsx';
import type { FieldMapping } from '../utils/field-mapping/types.tsx';
import { getIssueTypeNameFromGroupFilter } from '../../views/utils/filter-groups.tsx';
import {
	getFields,
	getAllFieldsByKey,
	getFieldMappings,
	getFieldMappingsAsList,
} from './fields.tsx';
import { getLocalIssueIdsByJiraIssueId, getIssueIdsConsideringArchived } from './issue-ids.tsx';
import {
	createSpecificallyMemoizedDataSelector,
	getIssueIdsInCreation,
} from './properties/index.tsx';
import { createGetIssueGroupIdentities } from './group-identities.tsx';
import { createGetConnectionFieldIssueIdsWithOptions } from './connection.tsx';

const EMPTY_FILTER: Array<Filter> = [];
const EMPTY_FILTER_GROUPS: Array<FilterGroup> = [];

export type IssueFilterFunction = (
	state: State,
	props: Props | undefined,
	localIssueId: LocalIssueId,
) => boolean;

type NumericGroupValue = {
	numericValue: number | undefined;
	operator: FilterOperator;
};

type ActiveFilterWithGroupValues = {
	field: Field;
	groupValues: (string | NumericGroupValue | undefined)[];
};

const getCurrentFilter = createSelector(
	getFields,
	(_state: State, props?: Props) => props?.filter,
	(fields, filter?: Filter[]): Filter[] => {
		if (!filter) {
			return EMPTY_FILTER;
		}
		return filter.filter((filterValue) => {
			// text filters do not have fields
			if (filterValue.type === TEXT_FIELD_FILTER) {
				return true;
			}

			return fields.some((field) => field.key === filterValue.field);
		});
	},
);

const getCurrentFilterGroups = createSelector(
	getFields,
	(_state: State, props?: Props) => props?.filterGroups,
	(fields, filterGroups?: FilterGroup[]): FilterGroup[] => {
		if (!filterGroups) {
			return EMPTY_FILTER_GROUPS;
		}
		return filterGroups.reduce<FilterGroup[]>((acc, group) => {
			const newFilters = group.filters.filter((filter) => {
				if (filter.type === TEXT_FIELD_FILTER) {
					return true;
				}

				return fields.some((field) => field.key === filter.field);
			});

			if (newFilters.length > 0) {
				acc.push({
					...group,
					filters: newFilters,
				});
			}

			return acc;
		}, []);
	},
);

export const getActiveFiltersGroupValuesOld = createSelector(
	getCurrentFilter,
	getAllFieldsByKey,
	(filters, fieldsByKey) => {
		const retVal: Array<ActiveFilterWithGroupValues> = [];
		filters.forEach((filter) => {
			if (filter.type === 'FIELD' && fieldsByKey[filter.field]) {
				retVal.push({
					field: fieldsByKey[filter.field],
					groupValues: filter.values.map(({ stringValue }) => stringValue),
				});
			} else if (filter.type === 'NUMBER' && fieldsByKey[filter.field]) {
				retVal.push({
					field: fieldsByKey[filter.field],
					groupValues: filter.values,
				});
			}
		});
		return retVal;
	},
);

// Works only with "All ideas" group (used to show/hide groups/columns/field values in groups/matrix config)
export const getActiveFiltersGroupValues = createSelector(
	getCurrentFilterGroups,
	getAllFieldsByKey,
	(groups, fieldsByKey) => {
		const retVal: Array<ActiveFilterWithGroupValues> = [];
		const allIdeasGroup = groups.find((group) => group.groupFilter === undefined);
		if (!allIdeasGroup) {
			return [];
		}

		allIdeasGroup.filters.forEach((filter) => {
			if (filter.type === 'FIELD' && fieldsByKey[filter.field]) {
				retVal.push({
					field: fieldsByKey[filter.field],
					groupValues: filter.values.map(({ stringValue }) => stringValue),
				});
			} else if (filter.type === 'NUMBER' && fieldsByKey[filter.field]) {
				retVal.push({
					field: fieldsByKey[filter.field],
					groupValues: filter.values,
				});
			}
		});
		return retVal;
	},
);

export const createQuickSearchFilter =
	({
		groupBy,
		verticalGroupBy,
		fieldMapping,
		connectionFieldKeys,
		filter,
	}: {
		groupBy?: FieldKey;
		verticalGroupBy?: FieldKey;
		connectionFieldKeys: FieldKey[];
		fieldMapping: FieldMapping<unknown>;
		filter: TextFilter;
	}): IssueFilterFunction =>
	(state, props, id) => {
		const filterFunc = fieldMapping.getFilter(filter);

		if (!filterFunc || isFunction(filterFunc)) {
			return false;
		}

		const jiraIdToLocalIssueId = getLocalIssueIdsByJiraIssueId(state, props);
		const identities = createGetIssueGroupIdentities({
			localIssueId: id,
			groupBy,
			verticalGroupBy,
		})(state, props);

		const connectionIds = connectionFieldKeys
			.map((fieldKey) => {
				const visibleConnections = identities
					.map(({ group, column }) =>
						createGetConnectionFieldIssueIdsWithOptions(fieldKey, id, {
							group,
							column,
						})(state, props),
					)
					.flat();

				return visibleConnections.map((c) => jiraIdToLocalIssueId[c.id]).filter(Boolean);
			})
			.flat();

		// check if issue summary or one of the issue connections matches the search filter
		return [id, ...connectionIds].some((issueId) =>
			filterFunc.filterFunction(
				fieldMapping.valueAccessor(state, props, issueId),
				state,
				props,
				issueId,
			),
		);
	};

const getFilterFunctionOld = createSelector(
	getCurrentFilter,
	getFieldMappingsAsList,
	(_: State, props?: Props) => props?.connectionFieldKeys,
	(_: State, props?: Props) => props?.verticalGroupBy,
	(_: State, props?: Props) => props?.groupBy,
	(
		filters: Filter[],
		fieldMappings: FieldMapping<unknown>[],
		connectionFieldKeys = [],
		verticalGroupBy,
		groupBy,
	): IssueFilterFunction => {
		return (state: State, props: Props | undefined, id: LocalIssueId) =>
			every(
				getResolvedFilters(filters, fieldMappings, groupBy, verticalGroupBy, connectionFieldKeys),
				(func: IssueFilterFunction) => func(state, props, id),
			);
	},
);

const getResolvedFilters = (
	filters: Filter[],
	fieldMappings: FieldMapping<unknown>[],
	groupBy: FieldKey | undefined,
	verticalGroupBy: FieldKey | undefined,
	connectionFieldKeys: FieldKey[],
) => {
	const conjunctFilters: Array<IssueFilterFunction> = [];
	const disjunctFilters: Record<string, Array<IssueFilterFunction>> = {};
	const quickFilters: Record<string, Array<IssueFilterFunction>> = {};

	filters.forEach((filter) => {
		fieldMappings.forEach((mapping: FieldMapping<unknown>) => {
			const filterValueFunc = mapping.getFilter(filter);
			if (filterValueFunc !== undefined) {
				if (isFunction(filterValueFunc)) {
					const filterFunc: IssueFilterFunction = (state, props, id) => {
						const value = mapping.valueAccessor(state, props, id);
						return filterValueFunc(value, state, props, id);
					};
					conjunctFilters.push(filterFunc);
				} else if (filterValueFunc.disjunctionIdentifier !== undefined) {
					const filterFunc: IssueFilterFunction = (state, props, id) => {
						const value = mapping.valueAccessor(state, props, id);
						return filterValueFunc.filterFunction(value, state, props, id);
					};
					if (!('localId' in filter) || filter.localId !== 'quick') {
						disjunctFilters[filterValueFunc.disjunctionIdentifier] = [
							...(disjunctFilters[filterValueFunc.disjunctionIdentifier] || []),
							filterFunc,
						];
					} else {
						// eslint-disable-next-line no-lonely-if
						if (fg('jpd-quick-search-for-connections-in-view')) {
							const quickSearchFilterFunc = createQuickSearchFilter({
								groupBy,
								verticalGroupBy,
								connectionFieldKeys,
								fieldMapping: mapping,
								filter,
							});

							quickFilters[filterValueFunc.disjunctionIdentifier] = [
								...(quickFilters[filterValueFunc.disjunctionIdentifier] || []),
								quickSearchFilterFunc,
							];
						} else {
							quickFilters[filterValueFunc.disjunctionIdentifier] = [
								...(quickFilters[filterValueFunc.disjunctionIdentifier] || []),
								filterFunc,
							];
						}
					}
				}
			}
		});
	});

	const resolvedQuickFilters = values(quickFilters).map(
		(filtersGroup): IssueFilterFunction =>
			(state, props, id) =>
				some(filtersGroup, (func) => func(state, props, id)),
	);

	const resolvedDisjunctFilters = values(disjunctFilters).map(
		(disjunctionGroup): IssueFilterFunction =>
			(state, props, id) =>
				some(disjunctionGroup, (func) => func(state, props, id)),
	);

	return [...conjunctFilters, ...resolvedDisjunctFilters, ...resolvedQuickFilters];
};

export const getIssueTypeFilter = (
	group: FilterGroup,
	fieldMappings: FieldMapping<unknown>[],
): IssueFilterFunction | undefined => {
	if (group.groupFilter !== undefined) {
		if (getIssueTypeNameFromGroupFilter(group.groupFilter) === undefined) {
			return;
		}

		const issueTypeMapping = fieldMappings.find(
			(mapping) => mapping.field?.type === FIELD_TYPES.ISSUE_TYPE,
		);

		if (issueTypeMapping === undefined) {
			return;
		}

		const issueTypeFilterFunc = issueTypeMapping.getFilter(group.groupFilter);

		if (!isFunction(issueTypeFilterFunc)) {
			return;
		}

		return (state, props, id) => {
			const value = issueTypeMapping.valueAccessor(state, props, id);
			return issueTypeFilterFunc(value, state, props, id);
		};
	}

	return () => true; // Always returns true for "All ideas" filters group
};

export const getFilterFunction = createSelector(
	getCurrentFilterGroups,
	getFieldMappingsAsList,
	(_: State, props?: Props) => props?.connectionFieldKeys,
	(_: State, props?: Props) => props?.verticalGroupBy,
	(_: State, props?: Props) => props?.groupBy,
	(
		groups,
		fieldMappings,
		connectionFieldKeys = [],
		verticalGroupBy,
		groupBy,
	): IssueFilterFunction => {
		const resolvedFilters: {
			group: IssueFilterFunction;
			issueType: IssueFilterFunction;
		}[] = [];

		groups.forEach((group) => {
			const issueTypeFilter = getIssueTypeFilter(group, fieldMappings);

			if (issueTypeFilter !== undefined) {
				resolvedFilters.push({
					group: (state, props, id) =>
						every(
							getResolvedFilters(
								group.filters,
								fieldMappings,
								groupBy,
								verticalGroupBy,
								connectionFieldKeys,
							),
							(func) => func(state, props, id),
						),
					issueType: issueTypeFilter,
				});
			}
		});

		return (state, props, id) => {
			const filtersMatchingIssueType = resolvedFilters.filter(
				(func) => func.issueType(state, props, id) === true,
			);

			if (filtersMatchingIssueType.length === 0) {
				return true;
			}

			return filtersMatchingIssueType.every(
				(filter) => filter.issueType(state, props, id) && filter.group(state, props, id),
			);
		};
	},
);

const getFilterRelevantFieldMappingsOld = createSelector(
	getCurrentFilter,
	getFieldMappingsAsList,
	(filters: Filter[], fieldMappings: FieldMapping<unknown>[]): FieldMapping<unknown>[] => {
		const mappings: Array<FieldMapping<unknown>> = [];
		filters.forEach((filter) => {
			fieldMappings.forEach((mapping) => {
				const filterValueFunc = mapping.getFilter(filter);
				if (filterValueFunc !== undefined) {
					mappings.push(mapping);
				}
			});
		});
		return mappings;
	},
);

const getFilterRelevantFieldMappings = createSelector(
	getCurrentFilterGroups,
	getFieldMappingsAsList,
	(groups: FilterGroup[], fieldMappings: FieldMapping<unknown>[]): FieldMapping<unknown>[] => {
		const mappings: Array<FieldMapping<unknown>> = [];
		groups.forEach((group) => {
			group.filters.forEach((filter) => {
				fieldMappings.forEach((mapping) => {
					const filterValueFunc = mapping.getFilter(filter);
					if (filterValueFunc !== undefined) {
						mappings.push(mapping);
					}
				});
			});
		});
		return mappings;
	},
);

const getFilterRelevantPropertiesOld = createSpecificallyMemoizedDataSelector(
	getFilterRelevantFieldMappingsOld,
);

const getFilterRelevantProperties = createSpecificallyMemoizedDataSelector(
	getFilterRelevantFieldMappings,
);

export const createIsInFilterResult = (
	fieldKey: FieldKey,
	filter: Filter | undefined,
	id: LocalIssueId,
) =>
	createSelector(
		getFieldMappings,
		(state) => state,
		(state: State, props: Props | undefined) => props,
		(fieldMappings, state, props) => {
			if (filter !== undefined) {
				const fieldMapping: FieldMapping<unknown> | undefined = fieldMappings[fieldKey];
				if (fieldMapping !== undefined) {
					const filterValueFunc = fieldMapping.getFilter(filter);
					if (filterValueFunc !== undefined) {
						if (isFunction(filterValueFunc)) {
							const value = fieldMapping.valueAccessor(state, props, id);
							return filterValueFunc(value, state, props, id);
						}

						if (filterValueFunc.disjunctionIdentifier !== undefined) {
							const value = fieldMapping.valueAccessor(state, props, id);
							return filterValueFunc.filterFunction(value, state, props, id);
						}
					}
				}
			}
			return false;
		},
	);

export const getFilteredIssueIds = functionWithCondition(
	() => fg('jpd_filter_groups_by_issue_type'),
	createSelector(
		getIssueIdsConsideringArchived,
		getFilterRelevantProperties,
		getFilterFunction,
		(issueIds: LocalIssueId[], [state, props], filter: IssueFilterFunction) =>
			issueIds.filter((id) => filter(state, props, id)),
	),
	createSelector(
		getIssueIdsConsideringArchived,
		getFilterRelevantPropertiesOld,
		getFilterFunctionOld,
		(issueIds: LocalIssueId[], [state, props], filter: IssueFilterFunction) =>
			issueIds.filter((id) => filter(state, props, id)),
	),
);

export const getFilteredIssueCount = createSelector(
	getFilteredIssueIds,
	getIssueIdsInCreation,
	(issueIds: LocalIssueId[], issueIdsInCreation: LocalIssueId[]) =>
		issueIds.filter((id) => !issueIdsInCreation.includes(id)).length,
);
